import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Save,
  Loader2,
  Plus,
  Trash2,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  Info,
  Boxes,
  XCircle,
  Pencil,
  X,
} from "lucide-react";

/**
 * Admin > IMS — iter203.7 rebuild.
 *
 * • IMS is now the inventory front door. Operator can enter physical
 *   inventory (including product / SKU / strength) without opening
 *   Product Edit first. Backend `POST /admin/inventory/lots` upserts
 *   the product shell / SKU / canonical strength on the fly.
 * • Sub-tabs: "Inventory Lots" (existing editable ledger, restyled) and
 *   "Add Inventory" (new full-form intake).
 * • New visual system: blue primary actions, cool-gray page bg, white
 *   cards, restrained semantic status colors. Scope-limited to IMS
 *   content; surrounding Admin shell is untouched.
 */

const TESTING_LABELS = { untested: "Untested", tested: "Tested" };

const TIERS = ["supply", "select"];

const TAB_BTN_ACTIVE =
  "border-b-2 border-blue-600 text-blue-600";
const TAB_BTN_IDLE =
  "border-b-2 border-transparent text-slate-600 hover:text-slate-900";

const PRIMARY_BTN =
  "inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 active:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm ring-1 ring-blue-700/10";
const SECONDARY_BTN =
  "inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-white border border-slate-300 text-slate-700 text-sm font-semibold hover:bg-slate-50 hover:border-slate-400 transition-colors";
const GHOST_BTN =
  "inline-flex items-center gap-2 px-3 py-2 rounded-md text-slate-600 text-sm font-medium hover:bg-slate-100 transition-colors";
const DANGER_ICON_BTN =
  "inline-flex items-center justify-center w-9 h-9 rounded-md text-slate-500 hover:text-red-600 hover:bg-red-50 transition-colors";

const CARD =
  "bg-white rounded-xl border border-slate-200 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_1px_3px_rgba(15,23,42,0.06)]";

export default function AdminInventory() {
  const [subTab, setSubTab] = useState("lots");
  const [reloadKey, setReloadKey] = useState(0);
  // iter206 — Tier inheritance: when the operator clicks a tier's
  // "Add Inventory" button, that tier is authoritative for the intake
  // form (overrides both the previous default and any saved draft).
  const [launchTier, setLaunchTier] = useState(null);
  const goToAdd = (tier) => {
    if (tier === "supply" || tier === "select") setLaunchTier(tier);
    setSubTab("add");
  };
  return (
    <div className="-mx-4 -my-4 bg-slate-100/70 min-h-[75vh]" data-testid="admin-inventory">
      <div className="px-8 py-8 max-w-[1320px] mx-auto">
        {/* Page header */}
        <div className="flex items-start justify-between mb-7 flex-wrap gap-3">
          <div>
            <h2
              className="text-[28px] leading-tight font-bold text-slate-900 tracking-tight"
              data-testid="ims-page-title"
            >
              Inventory Management System{" "}
              <span className="text-slate-500 font-medium">(IMS)</span>
            </h2>
            <p className="text-[15px] text-slate-600 mt-1.5 max-w-2xl">
              Enter, track, and manage all physical inventory. Decide where it goes after it's in IMS.
            </p>
          </div>
        </div>

        {/* Sub-tabs (iter203.9 — modest +18% type bump for legibility) */}
        <div className="border-b border-slate-200 mb-7 flex gap-8">
          <button
            onClick={() => setSubTab("lots")}
            className={`px-1 py-3 text-base font-bold tracking-tight transition-colors ${
              subTab === "lots" ? TAB_BTN_ACTIVE : TAB_BTN_IDLE
            }`}
            data-testid="ims-subtab-lots"
          >
            Inventory Lots
          </button>
          <button
            onClick={() => setSubTab("add")}
            className={`px-1 py-3 text-base font-bold tracking-tight transition-colors ${
              subTab === "add" ? TAB_BTN_ACTIVE : TAB_BTN_IDLE
            }`}
            data-testid="ims-subtab-add"
          >
            Add Inventory
          </button>
        </div>

        {subTab === "add" && (
          <AddInventoryForm
            initialTier={launchTier}
            onSaved={() => {
              setReloadKey((k) => k + 1);
              setLaunchTier(null);
              setSubTab("lots");
            }}
            onCancel={() => { setLaunchTier(null); setSubTab("lots"); }}
          />
        )}
        {subTab === "lots" && (
          <InventoryLotsPanel
            key={reloadKey}
            onGoToAdd={goToAdd}
          />
        )}
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────
 * Inventory Lots panel — existing editable ledger, restyled.
 * ────────────────────────────────────────────────────────────────── */

function InventoryLotsPanel({ onGoToAdd }) {
  return (
    <div className="space-y-8" data-testid="inventory-lots-panel">
      {TIERS.map((t) => (
        <InventoryTierTable key={t} tier={t} onGoToAdd={onGoToAdd} />
      ))}
    </div>
  );
}

function InventoryTierTable({ tier, onGoToAdd }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingLot, setEditingLot] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/inventory/${tier}`);
      setRows(data.rows || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || `Failed to load ${tier} inventory`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [tier]);

  // iter210 (IMS-inversion) — Both tiers: EDIT PEC deep-links to the
  // existing PEC edit dialog. CREATE PEC deep-links to a fresh New
  // Product dialog prefilled from the IMS lot itself. Never reads or
  // resurrects a dead PEC — a lot with `pec_deleted:true` or with a
  // missing linked product simply shows CREATE PEC.
  const openPec = (r) => {
    if (!r?.pec_id) return;
    const url = `/admin?tab=products&edit=${encodeURIComponent(r.pec_id)}`;
    window.location.assign(url);
  };
  const createPecFromLot = (r) => {
    if (!r?.lot_id) return;
    const url = `/admin?tab=products&create_from_lot=${encodeURIComponent(r.lot_id)}`;
    window.location.assign(url);
  };

  const onDeleteLot = async (r) => {
    const label = `${r.name}${r.variant_mg ? ` · ${r.variant_mg}${r.variant_unit || "mg"}` : ""} — ${r.supplier_name || "no vendor"}${r.batch_number ? ` / ${r.batch_number}` : ""}`;
    if (!window.confirm(`Delete this lot?\n\n${label}\n\nOnly this lot is removed. The public product stays in the catalog; its stock will drop by ${r.vials_in_stock} vial(s). Cannot be undone.`)) return;
    try {
      await api.delete(`/admin/inventory/lots/${r.lot_id}`);
      toast.success("Lot deleted");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Delete failed");
    }
  };

  const tierLabel = tier.charAt(0).toUpperCase() + tier.slice(1);
  const tierBadgeCls =
    tier === "select"
      ? "bg-amber-50 text-amber-700 ring-1 ring-amber-200"
      : "bg-blue-50 text-blue-700 ring-1 ring-blue-200";

  return (
    <div className={CARD} data-testid={`inventory-${tier}-table`}>
      <div className="flex items-center justify-between px-6 py-5 border-b border-slate-200 flex-wrap gap-3">
        <div className="flex items-baseline gap-3">
          <h3 className="text-xl font-bold text-slate-900 tracking-tight" data-testid={`inventory-${tier}-heading`}>
            Research {tierLabel}
          </h3>
          <span className={`inline-flex items-center px-2 py-0.5 text-xs font-bold uppercase tracking-wider rounded ${tierBadgeCls}`}>
            {tierLabel}
          </span>
          <span className="text-sm text-slate-500 font-medium">{rows.length} lots</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className={GHOST_BTN} data-testid={`inventory-${tier}-refresh`}>
            <RefreshCw size={14} strokeWidth={2} /> Refresh
          </button>
          <button
            onClick={() => onGoToAdd(tier)}
            className={PRIMARY_BTN}
            data-testid={`inventory-${tier}-add-lot`}
          >
            <Plus size={14} strokeWidth={2.5} /> Add Inventory
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        {loading && rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-500">
            No inventory on the {tierLabel} tier yet. Click{" "}
            <button
              onClick={() => onGoToAdd(tier)}
              className="underline font-semibold text-blue-700 hover:text-blue-800"
              data-testid={`inventory-${tier}-empty-add-inline`}
            >Add Inventory</button>{" "}
            to enter your first lot.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left">
                {["Product", "SKU", "Strength", "Vials", "CPV", "Lot / Batch", "Received", "Expiration", "Supplier", "Testing", "Status", "Actions"].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-3.5 text-[11px] font-bold uppercase tracking-[0.06em] text-slate-500"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => {
                const inStock = Number(r.vials_in_stock || 0) > 0;
                const testId = `${r.sku}-${r.variant_mg || "flat"}-${r.lot_id.slice(-6)}`;
                return (
                  <tr
                    key={r.lot_id}
                    className="bg-white hover:bg-slate-100 transition-colors"
                    data-testid={`inv-row-${testId}`}
                  >
                    <td className="px-4 py-3.5 text-[14px] text-slate-900 font-semibold">{r.name || "—"}</td>
                    <td className="px-4 py-3.5 font-mono text-[13px] font-semibold text-slate-900 whitespace-nowrap">{r.sku || "—"}</td>
                    <td className="px-4 py-3.5 font-mono text-[13px] font-semibold text-slate-800 whitespace-nowrap">
                      {r.variant_mg ? `${r.variant_mg}${r.variant_unit || "mg"}` : (r.dose_mg || "—")}
                    </td>
                    <td className="px-4 py-3.5 font-mono text-[14px] font-semibold text-slate-900" data-testid={`inv-vials-${testId}`}>
                      {r.vials_in_stock}
                    </td>
                    {/* iter206 — Read-only CPV column. Uses the SAME authoritative
                        methodology as the IMS Cost/CPV summary panel and the PO
                        calculator: line_total = product + testing + shipping + misc,
                        CPV = line_total / purchased_quantity. No new source, no
                        alternate calculation; if any breakdown field or purchased qty
                        is missing/zero, show em-dash rather than infer a value. */}
                    <td className="px-4 py-3.5 font-mono text-[13px] font-semibold text-slate-900 whitespace-nowrap" data-testid={`inv-cpv-${testId}`}>
                      {(() => {
                        // Only treat a field as present when its raw value is
                        // non-null/undefined; Number(null)===0 would otherwise
                        // silently fabricate a $0.00 CPV for lots with no cost
                        // breakdown captured at all.
                        const num = (v) => (v === null || v === undefined || v === "" ? NaN : Number(v));
                        const pc = num(r.product_cost);
                        const tc = num(r.testing_cost);
                        const sf = num(r.shipping_freight);
                        const mc = num(r.misc_cost);
                        const ppv = num(r.packaging_processing_per_vial);
                        const qty = num(r.purchased_quantity);
                        const anyCost = [pc, tc, sf, mc, ppv].some((v) => Number.isFinite(v));
                        if (!anyCost || !Number.isFinite(qty) || qty <= 0) return "—";
                        const packagingTotal = Number.isFinite(ppv) ? ppv * qty : 0;
                        const line_total = (Number.isFinite(pc) ? pc : 0) + (Number.isFinite(tc) ? tc : 0) + (Number.isFinite(sf) ? sf : 0) + (Number.isFinite(mc) ? mc : 0) + packagingTotal;
                        const cpv = line_total / qty;
                        return `$${cpv.toFixed(2)}`;
                      })()}
                    </td>
                    <td className="px-4 py-3.5 text-[13px] font-mono text-slate-700">{r.batch_number || "—"}</td>
                    <td className="px-4 py-3.5 text-[13px] text-slate-700 whitespace-nowrap">{formatDate(r.date_received)}</td>
                    <td className="px-4 py-3.5 text-[13px] text-slate-700 whitespace-nowrap">{formatDate(r.expiration_date)}</td>
                    <td className="px-4 py-3.5 text-[13px] text-slate-800">{r.supplier_name || "—"}</td>
                    <td className="px-4 py-3.5 text-xs">
                      <TestingBadge status={r.testing_status} />
                    </td>
                    <td className="px-4 py-3.5">
                      <StatusBadge inStock={inStock} />
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-1.5">
                        {/* iter210 — Both tiers, IMS-inversion.
                            EDIT PEC when a live PEC is linked
                            (row.pec_id set). CREATE PEC when the lot
                            has no live PEC (row.pec_id null OR
                            row.pec_deleted true — the IMS row is
                            standing alone). Never reads a dead PEC. */}
                        {(() => {
                          const isLive = !!r.pec_id && !r.pec_deleted;
                          const label = isLive ? "EDIT PEC" : "CREATE PEC";
                          const onClick = () => (isLive ? openPec(r) : createPecFromLot(r));
                          return (
                            <button
                              onClick={onClick}
                              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold ring-1 transition-colors ${
                                isLive
                                  ? "bg-slate-50 text-slate-700 hover:bg-slate-100 ring-slate-300"
                                  : "bg-amber-50 text-amber-800 hover:bg-amber-100 ring-amber-300"
                              }`}
                              data-testid={`ims-pec-${testId}`}
                              title={isLive
                                ? "Open the linked PEC in the Product Edit Card"
                                : "Create a new PEC from this IMS entry"}
                            >
                              {label}
                            </button>
                          );
                        })()}
                        <button
                          onClick={() => setEditingLot(r)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-blue-50 text-blue-700 text-xs font-semibold hover:bg-blue-100 hover:text-blue-800 ring-1 ring-blue-200 transition-colors"
                          data-testid={`inv-edit-${testId}`}
                        >
                          <Pencil size={12} strokeWidth={2.5} /> Edit
                        </button>
                        <button
                          onClick={() => onDeleteLot(r)}
                          className={DANGER_ICON_BTN}
                          data-testid={`inv-delete-${testId}`}
                          title="Delete this lot"
                          aria-label="Delete lot"
                        >
                          <Trash2 size={14} strokeWidth={2} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {editingLot && (
        <EditInventoryDialog
          lot={editingLot}
          tier={tier}
          onClose={() => setEditingLot(null)}
          onSaved={async () => { setEditingLot(null); await load(); }}
        />
      )}
    </div>
  );
}

function formatDate(v) {
  const s = String(v || "").trim();
  if (!s) return "—";
  return s.slice(0, 10);
}

function TestingBadge({ status }) {
  const isTested = String(status || "").toLowerCase() === "tested";
  if (isTested) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-50 text-green-700 text-[11px] font-medium ring-1 ring-green-200">
        <CheckCircle2 size={11} strokeWidth={2.5} /> Tested
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[11px] font-medium ring-1 ring-slate-200">
      Untested
    </span>
  );
}

function StatusBadge({ inStock }) {
  if (inStock) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-50 text-green-700 text-[11px] font-medium ring-1 ring-green-200">
        <CheckCircle2 size={11} strokeWidth={2.5} /> In stock
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-[11px] font-medium ring-1 ring-amber-200">
      <AlertCircle size={11} strokeWidth={2.5} /> Out
    </span>
  );
}

/* ────────────────────────────────────────────────────────────────────
 * Add Inventory form — new IMS-first intake.
 * ────────────────────────────────────────────────────────────────── */

// iter206 — Draft autosave key. Persists a single in-progress Add Inventory
// form across refreshes / tab closes / navigation. Cleared on successful save
// or explicit cancel-with-discard. Draft only — no server writes until the
// operator clicks Save Inventory.
const ADD_INV_DRAFT_KEY = "bbk-ims-add-inventory-draft";

function _loadDraft() {
  try {
    const raw = localStorage.getItem(ADD_INV_DRAFT_KEY);
    if (!raw) return null;
    const d = JSON.parse(raw);
    return d && typeof d === "object" ? d : null;
  } catch { return null; }
}
function _clearDraft() {
  try { localStorage.removeItem(ADD_INV_DRAFT_KEY); } catch { /* ignore */ }
}

function AddInventoryForm({ onSaved, onCancel, initialTier }) {
  // iter203.9 — IMS is the front door. Product is a blank operator-entered
  // field. We do NOT pull from Products / catalog / SELECT / SUPPLY.
  // iter206 — Any saved draft rehydrates every field. The launch tier
  // (initialTier) always overrides the draft tier so the button clicked
  // is authoritative.
  const _draft = _loadDraft() || {};
  const _resolvedTier = (initialTier === "supply" || initialTier === "select")
    ? initialTier
    : (_draft.productTier || "supply");
  const [productName, setProductName] = useState(_draft.productName || "");
  const [productTier, setProductTier] = useState(_resolvedTier);
  const [sku, setSku] = useState(_draft.sku || "");
  const [variantMg, setVariantMg] = useState(_draft.variantMg || "");
  // Inventory details — iter204.3 splits the single Quantity into
  // Purchased (drives CPV) and Actual Stock (drives availability).
  const [purchasedQuantity, setPurchasedQuantity] = useState(_draft.purchasedQuantity ?? "");
  const [actualStock, setActualStock] = useState(_draft.actualStock ?? "");
  const [actualStockTouched, setActualStockTouched] = useState(!!_draft.actualStockTouched);
  const [batchNumber, setBatchNumber] = useState(_draft.batchNumber || "");
  const [dateReceived, setDateReceived] = useState(_draft.dateReceived || new Date().toISOString().slice(0, 10));
  const [expirationDate, setExpirationDate] = useState(_draft.expirationDate || "");
  // Supplier
  const [supplierName, setSupplierName] = useState(_draft.supplierName || "");
  // iter203.10 — Cost / CPV breakdown replaces the flat unit-cost field.
  const [productCost, setProductCost] = useState(_draft.productCost ?? "");
  const [testingCost, setTestingCost] = useState(_draft.testingCost ?? "");
  const [shippingFreight, setShippingFreight] = useState(_draft.shippingFreight ?? "");
  const [miscCost, setMiscCost] = useState(_draft.miscCost ?? "");
  // iter214 — Packaging / Processing per vial. Scalar rate; multiplied by
  // Purchased Quantity to get Packaging/Processing Total; folded into
  // Total Landed Cost and CPV. Does not touch IMS inventory logic.
  const [packagingProcessingPerVial, setPackagingProcessingPerVial] = useState(_draft.packagingProcessingPerVial ?? "");
  // Additional
  const [testingStatus, setTestingStatus] = useState(_draft.testingStatus || "untested");
  const [capColor, setCapColor] = useState(_draft.capColor || "");
  const [notes, setNotes] = useState(_draft.notes || "");
  const [restoredNoticeVisible, setRestoredNoticeVisible] = useState(!!_draft.productName || !!_draft.sku || !!_draft.variantMg || !!_draft.purchasedQuantity);

  const [suppliers, setSuppliers] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/inventory/suppliers");
        setSuppliers(data?.suppliers || []);
      } catch (_) { /* silent */ }
    })();
  }, []);

  // iter206 — Persist every keystroke to localStorage as a draft. Never
  // touches the server; cleared on successful save.
  useEffect(() => {
    try {
      const draft = {
        productName, productTier, sku, variantMg,
        purchasedQuantity, actualStock, actualStockTouched,
        batchNumber, dateReceived, expirationDate,
        supplierName,
        productCost, testingCost, shippingFreight, miscCost, packagingProcessingPerVial,
        testingStatus, capColor, notes,
      };
      localStorage.setItem(ADD_INV_DRAFT_KEY, JSON.stringify(draft));
    } catch { /* ignore */ }
  }, [productName, productTier, sku, variantMg, purchasedQuantity, actualStock, actualStockTouched, batchNumber, dateReceived, expirationDate, supplierName, productCost, testingCost, shippingFreight, miscCost, packagingProcessingPerVial, testingStatus, capColor, notes]);

  const cpv = useMemo(() => {
    // iter204.3 — CPV denominator is Purchased Quantity. Actual Stock
    // MUST NOT influence CPV: selling or manually correcting inventory
    // does not retroactively change acquisition economics.
    // iter214 — Packaging/Processing is captured per vial; the total
    // contribution to landed cost is purchased_quantity × per-vial rate.
    const q = Number(purchasedQuantity) || 0;
    const p = Number(productCost) || 0;
    const t = Number(testingCost) || 0;
    const s = Number(shippingFreight) || 0;
    const m = Number(miscCost) || 0;
    const ppv = Number(packagingProcessingPerVial) || 0;
    const packagingTotal = q * ppv;
    const total = p + t + s + m + packagingTotal;
    return { total, cpv: q > 0 ? total / q : 0, packagingTotal };
  }, [purchasedQuantity, productCost, testingCost, shippingFreight, miscCost, packagingProcessingPerVial]);

  const canSubmit =
    !!productName.trim() &&
    !!variantMg.trim() &&
    purchasedQuantity !== "" && Number(purchasedQuantity) >= 0 &&
    actualStock !== "" && Number(actualStock) >= 0;

  const onPurchasedChange = (v) => {
    setPurchasedQuantity(v);
    if (!actualStockTouched) setActualStock(v);
  };
  const onActualChange = (v) => {
    setActualStock(v);
    setActualStockTouched(true);
  };

  const submit = async () => {
    setSaving(true);
    try {
      const body = {
        vials_in_stock: Number(actualStock || 0),
        purchased_quantity: Number(purchasedQuantity || 0),
        source: "supplier",
        testing_status: testingStatus,
        supplier_name: supplierName || null,
        batch_number: batchNumber || null,
        supplier_notes: notes || null,
        cap_color: capColor || null,
        date_received: dateReceived || null,
        expiration_date: expirationDate || null,
        unit_cost: cpv.cpv > 0 ? Number(cpv.cpv.toFixed(4)) : null,
        product_cost: productCost === "" ? null : Number(productCost),
        testing_cost: testingCost === "" ? null : Number(testingCost),
        shipping_freight: shippingFreight === "" ? null : Number(shippingFreight),
        misc_cost: miscCost === "" ? null : Number(miscCost),
        packaging_processing_per_vial: packagingProcessingPerVial === "" ? null : Number(packagingProcessingPerVial),
        variant_mg: variantMg ? String(variantMg).trim() : null,
        sku: sku ? String(sku).trim() : null,
        product_name: productName.trim(),
        product_tier: productTier,
      };
      const { data } = await api.post("/admin/inventory/lots", body);
      const savedUnit = (data?.lot?.variant_unit || "mg");
      const savedSku = data?.lot?.variant_mg
        ? `${sku || productName.trim() || "IMS"} · ${data.lot.variant_mg}${savedUnit}`
        : (sku || productName.trim() || "IMS");
      // iter206 — Clear draft on successful save so the next intake starts clean.
      _clearDraft();
      toast.success(`Inventory saved · ${savedSku}`);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const discardDraft = () => {
    if (!window.confirm("Discard the saved draft? All fields in this form will be cleared.")) return;
    _clearDraft();
    setProductName(""); setSku(""); setVariantMg("");
    setPurchasedQuantity(""); setActualStock(""); setActualStockTouched(false);
    setBatchNumber(""); setDateReceived(new Date().toISOString().slice(0, 10)); setExpirationDate("");
    setSupplierName("");
    setProductCost(""); setTestingCost(""); setShippingFreight(""); setMiscCost(""); setPackagingProcessingPerVial("");
    setTestingStatus("untested"); setCapColor(""); setNotes("");
    setRestoredNoticeVisible(false);
  };

  return (
    <div data-testid="add-inventory-form">
      {restoredNoticeVisible && (
        <div
          className="mb-4 rounded-lg bg-amber-50 border border-amber-300 px-4 py-3 flex items-start justify-between gap-3"
          data-testid="add-inv-draft-restored"
        >
          <div className="flex items-start gap-2">
            <Info size={16} className="text-amber-700 shrink-0 mt-0.5" strokeWidth={2.5} />
            <p className="text-sm text-amber-900">
              Draft restored — this form was saved locally the last time you left. Continue editing, or discard the draft.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={discardDraft}
              className="text-sm font-semibold text-amber-900 underline hover:text-amber-950"
              data-testid="add-inv-draft-discard"
            >
              Discard draft
            </button>
            <button
              onClick={() => setRestoredNoticeVisible(false)}
              className="text-amber-800 hover:text-amber-900"
              aria-label="Dismiss"
              data-testid="add-inv-draft-dismiss"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}
      <div className={`${CARD}`}>
        {/* Card header */}
        <div className="flex items-start justify-between px-7 py-6 border-b border-slate-200 flex-wrap gap-3">
          <div>
            <h3 className="text-[22px] leading-tight font-bold text-slate-900 tracking-tight">Add Inventory</h3>
            <p className="text-sm text-slate-600 mt-1.5">
              Enter what physically arrived. IMS is authoritative — commercial decisions happen afterward.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onCancel} className={SECONDARY_BTN} data-testid="add-inv-cancel">
              Cancel
            </button>
            <button
              onClick={submit}
              disabled={!canSubmit || saving}
              className={PRIMARY_BTN}
              data-testid="add-inv-submit"
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} strokeWidth={2.5} />}
              {saving ? "Saving…" : "Save Inventory"}
            </button>
          </div>
        </div>

        {/* Body — 2-column grid of 4 cards on desktop */}
        <div className="p-7 grid grid-cols-1 lg:grid-cols-2 gap-6 bg-slate-50/40">
          {/* 1. Product Information */}
          <FormCard title="1. Product Information">
            <FieldLabel required>Product</FieldLabel>
            <Input
              value={productName}
              onChange={(e) => setProductName(e.target.value)}
              className="h-10 text-sm"
              placeholder="e.g. Retatrutide"
              data-testid="add-inv-product-name"
              autoFocus
            />
            <p className="text-xs text-slate-500 mt-1">
              Operator-entered. IMS is authoritative — no auto-populate from catalog.
            </p>

            <div className="mt-4">
              <FieldLabel required>SKU</FieldLabel>
              <Input
                value={sku}
                onChange={(e) => setSku(e.target.value)}
                className="h-10 text-sm font-mono"
                placeholder="e.g. BBK-RETA-10"
                data-testid="add-inv-sku"
              />
              <p className="text-xs text-slate-500 mt-1">
                If the SKU already exists, this lot is added to that product. Otherwise a new product record is created.
              </p>
            </div>

            <div className="mt-4">
              <FieldLabel required>Strength</FieldLabel>
              <Input
                type="text"
                inputMode="decimal"
                value={variantMg}
                onChange={(e) => setVariantMg(e.target.value)}
                className="h-10 text-sm font-mono"
                placeholder="e.g. 10"
                data-testid="add-inv-mg-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                Numbers only — unit (mg / ml / mcg / iu) is inherited from the product's variant unit.
              </p>
            </div>

            <div className="mt-4">
              <FieldLabel>Tier</FieldLabel>
              <Select value={productTier} onValueChange={setProductTier}>
                <SelectTrigger className="h-10 text-sm" data-testid="add-inv-tier">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="supply">Supply</SelectItem>
                  <SelectItem value="select">Select</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">
                Applies only when a new product record is created (unknown SKU).
              </p>
            </div>
          </FormCard>

          {/* 2. Inventory Details */}
          <FormCard title="2. Inventory Details">
            <FieldLabel required>Purchased Quantity</FieldLabel>
            <Input
              type="number"
              min="0"
              value={purchasedQuantity}
              onChange={(e) => onPurchasedChange(e.target.value)}
              className="h-10 text-sm"
              placeholder="0"
              data-testid="add-inv-purchased-quantity"
            />
            <p className="text-xs text-slate-500 mt-1">
              Original quantity purchased for this lot. Used as the CPV denominator; does not decrease when inventory is sold.
            </p>

            <div className="mt-4">
              <FieldLabel required>Actual Stock Quantity</FieldLabel>
              <Input
                type="number"
                min="0"
                value={actualStock}
                onChange={(e) => onActualChange(e.target.value)}
                className="h-10 text-sm"
                placeholder="0"
                data-testid="add-inv-actual-stock"
              />
              <p className="text-xs text-slate-500 mt-1">
                Live physical vial count. Auto-mirrors Purchased until edited. Drives storefront availability.
              </p>
            </div>

            <div className="mt-4">
              <FieldLabel>Lot / Batch Number</FieldLabel>
              <Input
                value={batchNumber}
                onChange={(e) => setBatchNumber(e.target.value)}
                className="h-10 text-sm font-mono"
                placeholder="e.g. BP157-240918"
                data-testid="add-inv-batch"
              />
            </div>

            <div className="mt-4">
              <FieldLabel>Expiration Date</FieldLabel>
              <Input
                type="date"
                value={expirationDate}
                onChange={(e) => setExpirationDate(e.target.value)}
                className="h-10 text-sm"
                data-testid="add-inv-expiration"
              />
            </div>

            <div className="mt-4">
              <FieldLabel required>Received Date</FieldLabel>
              <Input
                type="date"
                value={dateReceived}
                onChange={(e) => setDateReceived(e.target.value)}
                className="h-10 text-sm"
                data-testid="add-inv-received"
              />
            </div>
          </FormCard>

          {/* 3. Supplier (iter203.10) */}
          <FormCard title="3. Supplier">
            <FieldLabel>Supplier</FieldLabel>
            <Input
              value={supplierName}
              onChange={(e) => setSupplierName(e.target.value)}
              list="ims-supplier-suggestions"
              className="h-10 text-sm"
              placeholder="e.g. OPTIMIZED INSIDER"
              data-testid="add-inv-supplier"
            />
            <datalist id="ims-supplier-suggestions">
              {suppliers.map((s) => <option key={s} value={s} />)}
            </datalist>
            <p className="text-xs text-slate-500 mt-1">
              Type a new name or pick from previously used suppliers.
            </p>
          </FormCard>

          {/* 4. Cost / CPV (iter203.10) */}
          <FormCard title="4. Cost / CPV">
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
              <div className="space-y-3">
                <div>
                  <FieldLabel>Product Cost (USD)</FieldLabel>
                  <Input type="number" min="0" step="0.01" value={productCost}
                    onChange={(e) => setProductCost(e.target.value)}
                    className="h-10 text-sm" placeholder="0.00"
                    data-testid="add-inv-product-cost" />
                </div>
                <div>
                  <FieldLabel>Testing Cost (USD)</FieldLabel>
                  <Input type="number" min="0" step="0.01" value={testingCost}
                    onChange={(e) => setTestingCost(e.target.value)}
                    className="h-10 text-sm" placeholder="0.00"
                    data-testid="add-inv-testing-cost" />
                </div>
                <div>
                  <FieldLabel>Shipping / Freight (USD)</FieldLabel>
                  <Input type="number" min="0" step="0.01" value={shippingFreight}
                    onChange={(e) => setShippingFreight(e.target.value)}
                    className="h-10 text-sm" placeholder="0.00"
                    data-testid="add-inv-shipping" />
                </div>
                <div>
                  <FieldLabel>Misc. Cost (USD)</FieldLabel>
                  <Input type="number" min="0" step="0.01" value={miscCost}
                    onChange={(e) => setMiscCost(e.target.value)}
                    className="h-10 text-sm" placeholder="0.00"
                    data-testid="add-inv-misc-cost" />
                </div>
                <div>
                  <FieldLabel>Packaging / Processing Per Vial (USD)</FieldLabel>
                  <Input type="number" min="0" step="0.01" value={packagingProcessingPerVial}
                    onChange={(e) => setPackagingProcessingPerVial(e.target.value)}
                    className="h-10 text-sm" placeholder="0.00"
                    data-testid="add-inv-packaging-per-vial" />
                  <p className="text-xs text-slate-500 mt-1">
                    Multiplied by Purchased Quantity for the Packaging / Processing total.
                  </p>
                </div>
              </div>
              <CostSummaryPanel
                productCost={Number(productCost) || 0}
                testingCost={Number(testingCost) || 0}
                shippingFreight={Number(shippingFreight) || 0}
                miscCost={Number(miscCost) || 0}
                packagingPerVial={Number(packagingProcessingPerVial) || 0}
                packagingTotal={cpv.packagingTotal}
                totalLanded={cpv.total}
                cpv={cpv.cpv}
                quantity={Number(purchasedQuantity) || 0}
                testId="add-inv"
              />
            </div>
          </FormCard>

          {/* 4. Additional Information (Optional) */}
          <FormCard title="4. Additional Information" subtitle="(Optional)">
            <FieldLabel>Testing Status</FieldLabel>
            <Select value={testingStatus} onValueChange={setTestingStatus}>
              <SelectTrigger className="h-10 text-sm" data-testid="add-inv-testing">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(TESTING_LABELS).map(([v, l]) => (
                  <SelectItem key={v} value={v}>{l}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="mt-4">
              <FieldLabel>Cap Color</FieldLabel>
              <Input
                value={capColor}
                onChange={(e) => setCapColor(e.target.value)}
                className="h-10 text-sm"
                placeholder="e.g. Red"
                data-testid="add-inv-cap"
              />
            </div>

            <div className="mt-4">
              <FieldLabel>Notes</FieldLabel>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full min-h-[92px] p-3 text-sm border border-slate-300 rounded-md focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none resize-y"
                placeholder="Lead time, QA jots, anything else…"
                data-testid="add-inv-notes"
                maxLength={500}
              />
              <p className="text-xs text-slate-500 mt-1 text-right">{notes.length}/500</p>
            </div>
          </FormCard>
        </div>

        {/* 5. What-If Pricing Scenarios */}
        <div className="px-7 pb-4">
          <WhatIfCard cpv={cpv.cpv} testId="add-inv" />
        </div>

        {/* Info banner */}
        <div className="mx-7 mb-7 rounded-lg bg-blue-50 border border-blue-200 px-4 py-3 flex items-start gap-3">
          <Info size={16} className="text-blue-600 shrink-0 mt-0.5" strokeWidth={2.5} />
          <p className="text-sm text-blue-900">
            After saving, this inventory lives in IMS. You can allocate it to <b>SELECT</b>, <b>SUPPLY</b>, or hold it in general inventory — commercial decisions happen after intake.
          </p>
        </div>
      </div>

      {/* What's Next stepper */}
      <div className={`${CARD} mt-6`} data-testid="ims-whats-next">
        <div className="px-6 py-5 flex items-center gap-6 flex-wrap">
          <div className="flex items-center gap-2">
            <Boxes size={18} className="text-slate-700" />
            <span className="text-sm font-bold text-slate-900">What's Next?</span>
          </div>
          <StepChip index={1} label="Add Inventory" description="Enter all inventory details in IMS" active />
          <StepChip index={2} label="Save to IMS" description="Inventory is now in your system" />
          <StepChip index={3} label="Decide Where It Goes" description="Allocate to SELECT, SUPPLY, or hold" />
        </div>
      </div>
    </div>
  );
}

function FormCard({ title, subtitle, children }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
      <h4 className="text-[15px] font-bold text-slate-900 mb-5 pb-3 border-b border-slate-100 tracking-tight">
        {title}
        {subtitle && <span className="text-slate-400 font-medium ml-1.5 text-sm">{subtitle}</span>}
      </h4>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function FieldLabel({ required, children }) {
  return (
    <label className="block text-[13px] font-semibold text-slate-800 mb-1.5">
      {children}
      {required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
  );
}

function StepChip({ index, label, description, active }) {
  return (
    <div className="flex items-center gap-2.5 min-w-0">
      <div
        className={`w-7 h-7 shrink-0 rounded-full flex items-center justify-center text-xs font-semibold ring-1 ${
          active
            ? "bg-green-100 text-green-700 ring-green-300"
            : "bg-slate-100 text-slate-500 ring-slate-200"
        }`}
      >
        {active ? <CheckCircle2 size={14} strokeWidth={2.5} /> : index}
      </div>
      <div className="min-w-0">
        <p className="text-sm font-semibold text-slate-900 leading-tight truncate">{label}</p>
        <p className="text-xs text-slate-500 leading-tight truncate">{description}</p>
      </div>
    </div>
  );
}


/* ────────────────────────────────────────────────────────────────────
 * Edit Inventory dialog — visual language locked to Add Inventory.
 * iter203.8 — IMS is authoritative: SKU / Product name / MG changes
 * write back to the product record so downstream systems read the
 * IMS-authoritative values. Save updates the same lot_id in place —
 * no duplicates.
 * ────────────────────────────────────────────────────────────────── */

function EditInventoryDialog({ lot, tier, onClose, onSaved }) {
  const isSupply = tier === "supply";
  const currentListingId = lot.product_id || "";
  const [listingId, setListingId] = useState(currentListingId);
  const [listings, setListings] = useState([]);
  const [productName, setProductName] = useState(lot.name || "");
  const [sku, setSku] = useState(lot.sku || "");
  const [variantMg, setVariantMg] = useState(
    lot.variant_mg == null ? "" : String(lot.variant_mg),
  );
  const [vialsPerKit, setVialsPerKit] = useState(
    lot.vials_per_kit == null ? "" : String(lot.vials_per_kit),
  );
  // iter204.3 — Purchased vs Actual are edited independently. Purchased
  // drives CPV. Actual is the live physical count.
  const [purchasedQuantity, setPurchasedQuantity] = useState(
    String(lot.purchased_quantity ?? lot.vials_in_stock ?? ""),
  );
  const [actualStock, setActualStock] = useState(String(lot.vials_in_stock ?? ""));
  const [batchNumber, setBatchNumber] = useState(lot.batch_number || "");
  const [dateReceived, setDateReceived] = useState(
    String(lot.date_received || "").slice(0, 10),
  );
  const [expirationDate, setExpirationDate] = useState(
    String(lot.expiration_date || "").slice(0, 10),
  );
  const [supplierName, setSupplierName] = useState(lot.supplier_name || "");
  const [productCost, setProductCost] = useState(lot.product_cost == null ? "" : String(lot.product_cost));
  const [testingCost, setTestingCost] = useState(lot.testing_cost == null ? "" : String(lot.testing_cost));
  const [shippingFreight, setShippingFreight] = useState(lot.shipping_freight == null ? "" : String(lot.shipping_freight));
  const [miscCost, setMiscCost] = useState(lot.misc_cost == null ? "" : String(lot.misc_cost));
  // iter214 — Packaging / Processing per vial.
  const [packagingProcessingPerVial, setPackagingProcessingPerVial] = useState(
    lot.packaging_processing_per_vial == null ? "" : String(lot.packaging_processing_per_vial)
  );
  const [testingStatus, setTestingStatus] = useState(lot.testing_status || "untested");
  const [capColor, setCapColor] = useState(lot.cap_color || "");
  const [notes, setNotes] = useState(lot.supplier_notes || "");
  const [saving, setSaving] = useState(false);
  const [suppliers, setSuppliers] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/inventory/suppliers");
        setSuppliers(data?.suppliers || []);
      } catch (_) { /* silent */ }
    })();
  }, []);

  useEffect(() => {
    if (!isSupply) return;
    (async () => {
      try {
        const [{ data }, { data: inv }] = await Promise.all([api.get("/admin/products"), api.get("/admin/inventory/supply")]);
        // Label = IMS product name as shown in the IMS table for that listing.
        const imsName = {};
        (inv?.rows || []).forEach((r) => { if (r.product_id && r.name && !imsName[r.product_id]) imsName[r.product_id] = r.name; });
        setListings((data || [])
          .filter((p) => p.tier === "supply" && Number(p.kit_vial_count || 0) !== 10 && p.active !== false && !p.is_draft && !p.archived)
          .map((p) => ({ ...p, label: imsName[p.id] || p.name })));
      } catch (_) { /* silent */ }
    })();
  }, [isSupply]);

  // ESC to close, lock body scroll while open.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const cpv = useMemo(() => {
    // iter204.3 — CPV denominator is Purchased Quantity (immutable acquisition
    // qty). Changes to Actual Stock never mutate this value.
    // iter214 — Packaging/Processing total = purchased_quantity × per-vial rate.
    const q = Number(purchasedQuantity) || 0;
    const p = Number(productCost) || 0;
    const t = Number(testingCost) || 0;
    const s = Number(shippingFreight) || 0;
    const m = Number(miscCost) || 0;
    const ppv = Number(packagingProcessingPerVial) || 0;
    const packagingTotal = q * ppv;
    const total = p + t + s + m + packagingTotal;
    return { total, cpv: q > 0 ? total / q : 0, packagingTotal };
  }, [purchasedQuantity, productCost, testingCost, shippingFreight, miscCost, packagingProcessingPerVial]);

  const canSubmit =
    purchasedQuantity !== "" && Number(purchasedQuantity) >= 0 &&
    actualStock !== "" && Number(actualStock) >= 0;

  const submit = async () => {
    setSaving(true);
    try {
      const body = {
        vials_in_stock: Number(actualStock || 0),
        purchased_quantity: Number(purchasedQuantity || 0),
        testing_status: testingStatus,
        supplier_name: supplierName,
        batch_number: batchNumber,
        supplier_notes: notes,
        cap_color: capColor,
        date_received: dateReceived,
        expiration_date: expirationDate,
        unit_cost: cpv.cpv > 0 ? Number(cpv.cpv.toFixed(4)) : null,
        product_cost: productCost === "" ? null : Number(productCost),
        testing_cost: testingCost === "" ? null : Number(testingCost),
        shipping_freight: shippingFreight === "" ? null : Number(shippingFreight),
        misc_cost: miscCost === "" ? null : Number(miscCost),
        packaging_processing_per_vial: packagingProcessingPerVial === "" ? null : Number(packagingProcessingPerVial),
        variant_mg: variantMg === "" ? null : String(variantMg).trim(),
        sku: sku === "" ? null : String(sku).trim(),
        product_name: productName === "" ? null : String(productName).trim(),
      };
      await api.put(`/admin/inventory/lots/${lot.lot_id}`, body);
      // Re-link sent alone so the existing endpoint touches nothing but the link.
      if (isSupply && listingId && listingId !== currentListingId) {
        await api.put(`/admin/inventory/lots/${lot.lot_id}`, { product_id: listingId });
      }
      toast.success(`Updated · ${sku || lot.sku || "lot"}${variantMg ? ` · ${variantMg}mg` : ""}`);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/50 backdrop-blur-sm px-4 py-8"
      onClick={onClose}
      data-testid="edit-inventory-dialog"
    >
      <div
        className="relative w-full max-w-6xl bg-white rounded-xl shadow-2xl border border-slate-200 my-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Dialog header */}
        <div className="flex items-start justify-between px-7 py-6 border-b border-slate-200 flex-wrap gap-3 bg-white rounded-t-xl">
          <div>
            <h3 className="text-[22px] leading-tight font-bold text-slate-900 tracking-tight">
              Edit Inventory
            </h3>
            <p className="text-sm text-slate-600 mt-1.5">
              IMS is authoritative. Changing SKU, product name, or strength updates the underlying product record.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className={SECONDARY_BTN} data-testid="edit-inv-cancel">
              Cancel
            </button>
            <button
              onClick={submit}
              disabled={!canSubmit || saving}
              className={PRIMARY_BTN}
              data-testid="edit-inv-submit"
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} strokeWidth={2.5} />}
              {saving ? "Saving…" : "Save Inventory"}
            </button>
            <button
              onClick={onClose}
              className="w-9 h-9 flex items-center justify-center rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors ml-1"
              data-testid="edit-inv-close"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Body — same 2-col visual language as Add Inventory */}
        <div className="p-7 grid grid-cols-1 lg:grid-cols-2 gap-6 bg-slate-50/40">
          {/* 1. Product Information */}
          <FormCard title="1. Product Information">
            {isSupply && (
              <div className="mb-4">
                <FieldLabel>Link to listing</FieldLabel>
                <select
                  value={listingId}
                  onChange={(e) => setListingId(e.target.value)}
                  className="h-10 w-full rounded-md border border-input bg-white px-3 text-sm"
                  data-testid="edit-inv-listing-select"
                >
                  {!listings.some((p) => p.id === currentListingId) && (
                    <option value={currentListingId}>{currentListingId ? "Current listing (not live)" : "Not linked"}</option>
                  )}
                  {listings.map((p) => (
                    <option key={p.id} value={p.id}>{p.label}</option>
                  ))}
                </select>
                <p className="text-xs text-slate-500 mt-1">
                  Live SUPPLY listings. Changing this re-links this lot only.
                </p>
              </div>
            )}
            <FieldLabel required>Product name</FieldLabel>
            <Input
              value={productName}
              onChange={(e) => setProductName(e.target.value)}
              className="h-10 text-sm"
              data-testid="edit-inv-product-name"
            />
            <p className="text-xs text-slate-500 mt-1">
              Editing this renames the underlying product record.
            </p>

            <div className="mt-4">
              <FieldLabel>SKU</FieldLabel>
              <Input
                value={sku}
                onChange={(e) => setSku(e.target.value)}
                className="h-10 text-sm font-mono"
                data-testid="edit-inv-sku"
              />
              <p className="text-xs text-slate-500 mt-1">
                Saved to the product record. Cross-product collisions are rejected.
              </p>
            </div>

            <div className="mt-4">
              <FieldLabel required>Strength</FieldLabel>
              <Input
                type="text"
                inputMode="decimal"
                value={variantMg}
                onChange={(e) => setVariantMg(e.target.value)}
                className="h-10 text-sm font-mono"
                data-testid="edit-inv-mg"
              />
              <p className="text-xs text-slate-500 mt-1">
                Numbers only — unit (mg / ml / mcg / iu) is inherited from the product's variant unit. IMS-authoritative.
              </p>
            </div>
          </FormCard>

          {/* 2. Inventory Details */}
          <FormCard title="2. Inventory Details">
            <FieldLabel required>Purchased Quantity</FieldLabel>
            <Input
              type="number"
              min="0"
              value={purchasedQuantity}
              onChange={(e) => setPurchasedQuantity(e.target.value)}
              className="h-10 text-sm"
              data-testid="edit-inv-purchased-quantity"
            />
            <p className="text-xs text-slate-500 mt-1">
              Acquisition qty used for CPV. Independent of Actual Stock.
            </p>
            <div className="mt-4">
              <FieldLabel required>Actual Stock Quantity</FieldLabel>
              <Input
                type="number"
                min="0"
                value={actualStock}
                onChange={(e) => setActualStock(e.target.value)}
                className="h-10 text-sm"
                data-testid="edit-inv-actual-stock"
              />
              <p className="text-xs text-slate-500 mt-1">
                Current physical vial count. Editable for manual corrections. Does NOT change CPV.
              </p>
            </div>
            <div className="mt-4">
              <FieldLabel>Lot / Batch Number</FieldLabel>
              <Input
                value={batchNumber}
                onChange={(e) => setBatchNumber(e.target.value)}
                className="h-10 text-sm font-mono"
                data-testid="edit-inv-batch"
              />
            </div>
            <div className="mt-4">
              <FieldLabel>Expiration Date</FieldLabel>
              <Input
                type="date"
                value={expirationDate}
                onChange={(e) => setExpirationDate(e.target.value)}
                className="h-10 text-sm"
                data-testid="edit-inv-expiration"
              />
            </div>
            <div className="mt-4">
              <FieldLabel required>Received Date</FieldLabel>
              <Input
                type="date"
                value={dateReceived}
                onChange={(e) => setDateReceived(e.target.value)}
                className="h-10 text-sm"
                data-testid="edit-inv-received"
              />
            </div>
          </FormCard>

          {/* 3. Supplier (iter203.10) */}
          <FormCard title="3. Supplier">
            <FieldLabel required>Supplier</FieldLabel>
            <Input
              value={supplierName}
              onChange={(e) => setSupplierName(e.target.value)}
              list="ims-supplier-suggestions-edit"
              className="h-10 text-sm"
              placeholder="e.g. OPTIMIZED INSIDER"
              data-testid="edit-inv-supplier"
            />
            <datalist id="ims-supplier-suggestions-edit">
              {suppliers.map((s) => <option key={s} value={s} />)}
            </datalist>
            <p className="text-xs text-slate-500 mt-1">
              Type a new name or pick from previously used suppliers.
            </p>
          </FormCard>

          {/* 4. Cost / CPV (iter203.10) */}
          <FormCard title="4. Cost / CPV">
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
              <div className="space-y-3">
                <div>
                  <FieldLabel required>Product Cost (USD)</FieldLabel>
                  <Input type="number" min="0" step="0.01" value={productCost}
                    onChange={(e) => setProductCost(e.target.value)}
                    className="h-10 text-sm" placeholder="0.00"
                    data-testid="edit-inv-product-cost" />
                </div>
                <div>
                  <FieldLabel>Testing Cost (USD)</FieldLabel>
                  <Input type="number" min="0" step="0.01" value={testingCost}
                    onChange={(e) => setTestingCost(e.target.value)}
                    className="h-10 text-sm" placeholder="0.00"
                    data-testid="edit-inv-testing-cost" />
                </div>
                <div>
                  <FieldLabel>Shipping / Freight (USD)</FieldLabel>
                  <Input type="number" min="0" step="0.01" value={shippingFreight}
                    onChange={(e) => setShippingFreight(e.target.value)}
                    className="h-10 text-sm" placeholder="0.00"
                    data-testid="edit-inv-shipping" />
                </div>
                <div>
                  <FieldLabel>Misc. Cost (USD)</FieldLabel>
                  <Input type="number" min="0" step="0.01" value={miscCost}
                    onChange={(e) => setMiscCost(e.target.value)}
                    className="h-10 text-sm" placeholder="0.00"
                    data-testid="edit-inv-misc-cost" />
                </div>
                <div>
                  <FieldLabel>Packaging / Processing Per Vial (USD)</FieldLabel>
                  <Input type="number" min="0" step="0.01" value={packagingProcessingPerVial}
                    onChange={(e) => setPackagingProcessingPerVial(e.target.value)}
                    className="h-10 text-sm" placeholder="0.00"
                    data-testid="edit-inv-packaging-per-vial" />
                  <p className="text-xs text-slate-500 mt-1">
                    Multiplied by Purchased Quantity for the Packaging / Processing total.
                  </p>
                </div>
              </div>
              <CostSummaryPanel
                productCost={Number(productCost) || 0}
                testingCost={Number(testingCost) || 0}
                shippingFreight={Number(shippingFreight) || 0}
                miscCost={Number(miscCost) || 0}
                packagingPerVial={Number(packagingProcessingPerVial) || 0}
                packagingTotal={cpv.packagingTotal}
                totalLanded={cpv.total}
                cpv={cpv.cpv}
                quantity={Number(purchasedQuantity) || 0}
                testId="edit-inv"
              />
            </div>
          </FormCard>

          {/* 4. Additional Information */}
          <FormCard title="4. Additional Information" subtitle="(Optional)">
            <FieldLabel>Testing Status</FieldLabel>
            <Select value={testingStatus} onValueChange={setTestingStatus}>
              <SelectTrigger className="h-10 text-sm" data-testid="edit-inv-testing">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(TESTING_LABELS).map(([v, l]) => (
                  <SelectItem key={v} value={v}>{l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="mt-4">
              <FieldLabel>Cap Color</FieldLabel>
              <Input
                value={capColor}
                onChange={(e) => setCapColor(e.target.value)}
                className="h-10 text-sm"
                data-testid="edit-inv-cap"
              />
            </div>
            <div className="mt-4">
              <FieldLabel>Notes</FieldLabel>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full min-h-[92px] p-3 text-sm border border-slate-300 rounded-md focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none resize-y"
                data-testid="edit-inv-notes"
                maxLength={500}
              />
              <p className="text-xs text-slate-500 mt-1 text-right">{notes.length}/500</p>
            </div>
          </FormCard>
        </div>

        {/* 5. What-If Pricing Scenarios */}
        <div className="px-7 pb-7">
          <WhatIfCard cpv={cpv.cpv} testId="edit-inv" />
        </div>

        <div className="mx-7 mb-7 rounded-lg bg-blue-50 border border-blue-200 px-4 py-3 flex items-start gap-3">
          <Info size={16} className="text-blue-600 shrink-0 mt-0.5" strokeWidth={2.5} />
          <p className="text-sm text-blue-900">
            Saving updates this <b>same</b> inventory record (lot <span className="font-mono text-xs">{lot.lot_id.slice(-8)}</span>). SKU / product name / MG changes flow to the product record for downstream systems.
          </p>
        </div>
      </div>
    </div>
  );
}


/* iter203.10 — Cost/CPV summary panel + What-If pricing card.
 * CPV methodology mirrors the existing PO calculator:
 *   shipping_share = shipping × line_qty / total_qty
 *   line_total     = product + testing + misc + shipping_share
 *   CPV            = line_total / qty  (4 decimals)
 * For a single-lot IMS entry, line_qty == total_qty so the full
 * shipping cost lands on this record — matches the existing math.
 */

function CostSummaryPanel({ productCost, testingCost, shippingFreight, miscCost, packagingPerVial, packagingTotal, totalLanded, cpv, quantity, testId }) {
  const fmt = (v) => `$${(v || 0).toFixed(2)}`;
  return (
    <div className="rounded-lg bg-blue-50/40 border border-blue-100 p-4 self-start">
      <div className="space-y-1.5 text-sm">
        <div className="flex justify-between"><span className="text-slate-700">Product Cost</span><span className="font-mono text-slate-900">{fmt(productCost)}</span></div>
        <div className="flex justify-between"><span className="text-slate-700">Testing Cost</span><span className="font-mono text-slate-900">{fmt(testingCost)}</span></div>
        <div className="flex justify-between"><span className="text-slate-700">Shipping / Freight</span><span className="font-mono text-slate-900">{fmt(shippingFreight)}</span></div>
        <div className="flex justify-between"><span className="text-slate-700">Misc. Cost</span><span className="font-mono text-slate-900">{fmt(miscCost)}</span></div>
        <div className="flex justify-between">
          <span className="text-slate-700">
            Packaging / Processing
            <span className="text-slate-500 text-xs ml-1">({fmt(packagingPerVial || 0)} × {quantity || 0})</span>
          </span>
          <span className="font-mono text-slate-900" data-testid={`${testId}-packaging-total`}>{fmt(packagingTotal)}</span>
        </div>
      </div>
      <div className="border-t border-blue-200 mt-3 pt-3 flex justify-between text-sm">
        <span className="font-semibold text-slate-900">Total Landed Cost</span>
        <span className="font-mono font-bold text-slate-900" data-testid={`${testId}-total-landed`}>{fmt(totalLanded)}</span>
      </div>
      <div className="mt-3 rounded-md bg-green-50 border border-green-200 px-3 py-3">
        <div className="text-xs font-semibold text-slate-700">Cost Per Vial (CPV)</div>
        <div className="text-3xl font-bold text-green-700 font-mono tracking-tight" data-testid={`${testId}-cpv`}>
          ${(cpv || 0).toFixed(4)}
        </div>
        <div className="text-xs text-slate-500 mt-1">Based on {quantity} purchased vials</div>
      </div>
    </div>
  );
}

function WhatIfCard({ cpv, testId }) {
  const [open, setOpen] = useState(false);
  const [vialsPerKit, setVialsPerKit] = useState("10");
  const [kitPrice, setKitPrice] = useState("");
  const [markupPct, setMarkupPct] = useState("");

  const kitCpv = useMemo(() => (Number(cpv) || 0) * (Number(vialsPerKit) || 0), [cpv, vialsPerKit]);
  const derivedPrice = useMemo(() => {
    if (kitPrice) return Number(kitPrice) || 0;
    if (markupPct) return kitCpv * (1 + (Number(markupPct) || 0) / 100);
    return 0;
  }, [kitPrice, markupPct, kitCpv]);
  const marginUsd = derivedPrice - kitCpv;
  const marginPct = derivedPrice > 0 ? (marginUsd / derivedPrice) * 100 : 0;

  return (
    <div className="rounded-lg bg-blue-50 border border-blue-200" data-testid={`${testId}-whatif`}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-4"
        data-testid={`${testId}-whatif-toggle`}
      >
        <div className="flex items-center gap-3 text-left">
          <span className="w-8 h-8 rounded-md bg-blue-600 text-white flex items-center justify-center text-sm font-bold">5</span>
          <div>
            <div className="text-[15px] font-bold text-slate-900">What-If Pricing Scenarios <span className="text-slate-500 font-medium text-sm">(Optional)</span></div>
            <div className="text-xs text-slate-600">Model margins, markup, and kit pricing. Does not affect inventory.</div>
          </div>
        </div>
        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-white text-blue-700 text-xs font-semibold ring-1 ring-blue-200">
          {open ? "Close" : "Open What-If Calculator"}
        </span>
      </button>
      {open && (
        <div className="px-5 pb-5 pt-1 border-t border-blue-200 bg-white/70">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <FieldLabel>Vials per Kit (pricing)</FieldLabel>
              <Input type="number" min="1" value={vialsPerKit}
                onChange={(e) => setVialsPerKit(e.target.value)}
                className="h-10 text-sm" data-testid={`${testId}-whatif-vpk`} />
            </div>
            <div>
              <FieldLabel>Retail Markup %</FieldLabel>
              <Input type="number" min="0" step="1" value={markupPct}
                onChange={(e) => { setMarkupPct(e.target.value); setKitPrice(""); }}
                className="h-10 text-sm" placeholder="e.g. 200"
                data-testid={`${testId}-whatif-markup`} />
            </div>
            <div>
              <FieldLabel>OR Kit Sell Price (USD)</FieldLabel>
              <Input type="number" min="0" step="0.01" value={kitPrice}
                onChange={(e) => { setKitPrice(e.target.value); setMarkupPct(""); }}
                className="h-10 text-sm" placeholder="0.00"
                data-testid={`${testId}-whatif-price`} />
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <ScenarioStat label="Kit CPV" value={`$${kitCpv.toFixed(2)}`} testId={`${testId}-whatif-kitcpv`} />
            <ScenarioStat label="Kit Sell Price" value={`$${derivedPrice.toFixed(2)}`} testId={`${testId}-whatif-derived`} />
            <ScenarioStat label="Margin $" value={`$${marginUsd.toFixed(2)}`} tone={marginUsd >= 0 ? "green" : "red"} testId={`${testId}-whatif-margin-usd`} />
            <ScenarioStat label="Margin %" value={`${marginPct.toFixed(1)}%`} tone={marginPct >= 0 ? "green" : "red"} testId={`${testId}-whatif-margin-pct`} />
          </div>
          <p className="text-xs text-slate-500 mt-3">Same CPV methodology as the existing CPV calculator. Modeling only — does not persist.</p>
        </div>
      )}
    </div>
  );
}

function ScenarioStat({ label, value, tone, testId }) {
  const toneCls = tone === "green" ? "text-green-700" : tone === "red" ? "text-red-700" : "text-slate-900";
  return (
    <div className="rounded-md border border-slate-200 bg-white px-3 py-2.5">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-lg font-bold font-mono ${toneCls}`} data-testid={testId}>{value}</div>
    </div>
  );
}
